// src/pages/VitAssegnati.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { authFetch } from '../utils/authFetch';
import { generateVitPdfBlob } from '../pdf/clientPdf';
import { salvaOffline, senzaRete, TIPI } from '../offline/salvaOffline';
import { Card } from '../components/ui';
import DatiTrasferta, { CAMPI_TRASFERTA } from '../components/DatiTrasferta';

// const BACKEND_URL = 'http://localhost:3001';

// Base per chiamate relative a gas, notifiche e lavorazioni
const GAS_API_BASE = '/api';
const API_BASE = '/api';

const templates = [
  { id: 'CIESSE_IMPIANTI', label: 'Ciesse Impianti', file: '/VIT/CiesseImpianti.pdf' },
  { id: 'CIESSE_SERVICE', label: 'Ciesse Service', file: '/VIT/CiesseService.pdf' },
];

const formatEuro = (val) => {
  if (val === '' || val === null || val === undefined) return '';
  const normalized = String(val).replace(',', '.');
  const num = parseFloat(normalized);
  if (!Number.isFinite(num)) return '';
  return num.toFixed(2).replace('.', ',');
};

// Nome visuale dell'utente per le notifiche (senza email)
function getCleanUserName(user) {
  if (!user) return '';

  const candidates = [];

  if (user.username) candidates.push(user.username);
  if (user.nome || user.cognome) {
    candidates.push(`${user.nome || ''} ${user.cognome || ''}`.trim());
  }
  if (user.fullName) candidates.push(user.fullName);

  let name = candidates.find(Boolean) || '';

  name = name
    .split(/\s+/)
    .filter((part) => !part.includes('@'))
    .join(' ')
    .trim();

  if (name) return name;

  if (user.email && user.email.includes('@')) {
    const local = user.email.split('@')[0];
    return local.replace('.', ' ').trim();
  }

  return '';
}

// Data/ora in formato "17/11/2025 alle ore 18:55"
function formatNow() {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const yyyy = now.getFullYear();
  const hh = String(now.getHours()).padStart(2, '0');
  const min = String(now.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} alle ore ${hh}:${min}`;
}

function VitAssegnati() {
  const location = useLocation();
  const navigate = useNavigate();

  const fromWorkOrder = location.state?.fromWorkOrder || {};

  // 🔹 log note proveniente dal backend (WorkInProgress.formData.noteGeneraliLog)
  let initialNoteLog = [];

  const rawLog =
    //fromWorkOrder.formData?.noteGeneraliLog ||
    //fromWorkOrder.noteGeneraliLog;
    fromWorkOrder?.formData?.noteGeneraliLog ||
    fromWorkOrder?.noteGeneraliLog;

  if (Array.isArray(rawLog)) {
    initialNoteLog = rawLog;
  }

  // 🔹 se non c'è ancora nessun log, uso la nota iniziale della lavorazione
  if (initialNoteLog.length === 0) {
    const initialText =
      fromWorkOrder.noteGenerali ||
      fromWorkOrder.descrizione ||
      fromWorkOrder.description ||
      '';

    if (initialText) {
      initialNoteLog = [
        {
          text: initialText,
          userId: null,
          userName: 'Creazione lavorazione',
          createdAt: fromWorkOrder.createdAt || null,
          iteration:
            typeof fromWorkOrder.iteration === 'number'
              ? fromWorkOrder.iteration
              : 0,
        },
      ];
    }
  }

  const workOrderId = location.state?.workOrderId || null;

  // === CODICE INTERNO COMPLETO CON ITERAZIONE ===
  const baseCode =
    fromWorkOrder.codiceInterno || fromWorkOrder.internalCode || '';
  const iteration =
    typeof fromWorkOrder.iteration === 'number'
      ? fromWorkOrder.iteration
      : 0;

  const fullInternalCode =
    iteration > 0 ? `${baseCode}-${iteration}` : baseCode;


  // =======================
  // Prefill dai dati assegnati
  // =======================
  const prefillData = () => {
    // Data = data di assegnazione intervento
    let data = '';
    if (fromWorkOrder.dateIntervento) {
      const d = new Date(fromWorkOrder.dateIntervento);
      if (!Number.isNaN(d)) data = d.toISOString().slice(0, 10);
    } else if (fromWorkOrder.dataIntervento) {
      const d = new Date(fromWorkOrder.dataIntervento);
      if (!Number.isNaN(d)) data = d.toISOString().slice(0, 10);
    } else if (fromWorkOrder.data) {
      // fallback se già in formato yyyy-mm-dd
      data = fromWorkOrder.data;
    }

    return {
      ...CAMPI_TRASFERTA,

      // DATI INTERVENTO
      data,
      cliente: fromWorkOrder.cliente || fromWorkOrder.companyName || '',
      // Istituto / impianto / presidio
      impianto:
        fromWorkOrder.impianto ||
        fromWorkOrder.presidio ||
        fromWorkOrder.reparto ||
        fromWorkOrder.equipmentPresidio ||
        '',
      // Indirizzo / luogo intervento
      luogo:
        fromWorkOrder.luogoIntervento ||
        fromWorkOrder.luogo ||
        fromWorkOrder.indirizzo ||
        fromWorkOrder.place ||
        '',
      telefono: fromWorkOrder.telefono || fromWorkOrder.customerPhone || '',
      tecnico: '', // lo compili a mano
      // Riferimento chiamata (codice esterno)
      numeroIntervento:
        fromWorkOrder.riferimentoChiamata ||
        fromWorkOrder.codiceEsterno ||
        fromWorkOrder.externalCode ||
        '',
      matricolaImpianto:
        fromWorkOrder.matricola ||
        fromWorkOrder.seriale ||
        fromWorkOrder.equipmentSerial ||
        '',
      modello:
        fromWorkOrder.modello || fromWorkOrder.equipmentModel || '',
      inventario:
        fromWorkOrder.inventario || fromWorkOrder.equipmentInventory || '',
      noteGenerali: '',

      // COSTI
      dirittoFissoQuantita: '1',
      dirittoFissoEuro: '',
      oreLavoroQuantita: '',
      oreLavoroEuro: '',

      // Ricambi
      ricambi: Array.from({ length: 8 }, () => ({
        quantita: '',
        descrizione: '',
        euro: '',
      })),

      // Lavori eseguiti
      lavoriEseguiti: '',
    };
  };

  const [selectedTemplate, setSelectedTemplate] =
    useState('CIESSE_IMPIANTI');

  // Dati intervento
  const [formData, setFormData] = useState(prefillData);
  const [noteLog, setNoteLog] = useState(initialNoteLog);

  const [tecnicoSignature, setTecnicoSignature] = useState(null);
  const [clienteSignature, setClienteSignature] = useState(null);

  const [signModal, setSignModal] = useState({ open: false, type: null });
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [currentUserName, setCurrentUserName] = useState('');

  const pdfUrl =
    templates.find((t) => t.id === selectedTemplate)?.file;

  // ======================
  // 🔥 RICARICA STATO SALVATO (WorkInProgress) quando riapro un già compilato
  // ======================
  useEffect(() => {
    const loadWip = async () => {
      try {
        if (!workOrderId) return;
      
        const res = await authFetch(
          `${API_BASE}/work/in-progress/by-workorder/${workOrderId}`
        );
      
        if (!res.ok) {
          console.warn('WIP non trovato o errore:', res.status);
          return;
        }
      
        const wipRaw = await res.json();
        const wip = wipRaw?.workInProgress || wipRaw;
      
        // 1) formData (riprendo tutto quello che avevi compilato)
        if (wip?.formData) {
          setFormData((prev) => ({
            ...prev,
            ...wip.formData,
            // data: tieni quella salvata se c'è
            data: wip.formData.data || prev.data,
          }));
        
          // 2) note log (se lo stai salvando dentro formData.noteGeneraliLog)
          const log =
            wip.formData.noteGeneraliLog ||
            wip.noteGeneraliLog ||
            null;
        
          if (Array.isArray(log)) setNoteLog(log);
        }
      
        // 3) firme
        if (wip?.tecnicoSignature) setTecnicoSignature(wip.tecnicoSignature);
        if (wip?.clienteSignature) setClienteSignature(wip.clienteSignature);
      } catch (err) {
        console.error('Errore recupero WorkInProgress (VitAssegnati):', err);
      }
    };
  
    loadWip();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workOrderId]);
  

  // ======================
  // Gas utilizzato / recuperato
  // ======================
  const [gasUsedName, setGasUsedName] = useState('');
  const [gasUsedAmount, setGasUsedAmount] = useState('');

  const [gasRecoveredName, setGasRecoveredName] = useState('');
  const [gasRecoveredAmount, setGasRecoveredAmount] = useState('');

  // Lista gas disponibili sul TUO furgone
  const [availableGases, setAvailableGases] = useState([]);

  useEffect(() => {
    const loadMyGases = async () => {
      try {
        // 1) utente corrente
        const meRes = await authFetch(`${GAS_API_BASE}/user`);
        if (!meRes.ok) {
          console.error(
            'Impossibile recuperare utente per lista gas (VIT ASSEGNATI)'
          );
          return;
        }
        const me = await meRes.json();
        const currentUserId = me.id || me._id;

        const displayName =
          `${me.nome || ''} ${me.cognome || ''}`.trim() || me.username || '';
        setCurrentUserName(displayName);


        // 2) tutti i livelli gas
        const gasRes = await authFetch(`${GAS_API_BASE}/gas`);
        if (!gasRes.ok) {
          console.error(
            'Impossibile recuperare lista gas (VIT ASSEGNATI)'
          );
          return;
        }
        const gasData = await gasRes.json();

        // 3) prendo solo i gas del MIO furgone
        const myGasNames = gasData
          .filter((g) => {
            const uid = g.user?._id || g.user?.id;
            return uid === currentUserId;
          })
          .map((g) => g.gasName);

        const uniqueNames = [...new Set(myGasNames)].sort();
        setAvailableGases(uniqueNames);

        if (!gasUsedName && uniqueNames.length === 1) {
          setGasUsedName(uniqueNames[0]);
        }
      } catch (err) {
        console.error(
          'Errore caricamento gas disponibili (VIT ASSEGNATI):',
          err
        );
      }
    };

    loadMyGases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* --------- Blocca scroll per modal firma --------- */
  useEffect(() => {
    if (signModal.open) {
      const originalOverflow = document.body.style.overflow;
      document.body.dataset.prevOverflow = originalOverflow || '';
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow =
          document.body.dataset.prevOverflow || '';
      };
    } else {
      document.body.style.overflow =
        document.body.dataset.prevOverflow || '';
    }
  }, [signModal.open]);

  /* --------- handlers form --------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleQuantityChange = (fieldName) => (e) => {
    const raw = e.target.value || '';
    const filtered = raw.replace(/\D/g, '').slice(0, 2);
    setFormData((prev) => ({ ...prev, [fieldName]: filtered }));
  };

  const handleRicambioChange = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.ricambi];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, ricambi: updated };
    });
  };

  /* --------- firma (canvas) --------- */
  const getPos = (canvas, evt) => {
    const rect = canvas.getBoundingClientRect();
    const isTouch = evt.touches && evt.touches[0];
    const clientX = isTouch ? evt.touches[0].clientX : evt.clientX;
    const clientY = isTouch ? evt.touches[0].clientY : evt.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDraw = (evt) => {
    evt.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#000';
    const pos = getPos(canvas, evt);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
  };

  const moveDraw = (evt) => {
    if (!isDrawing) return;
    evt.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const pos = getPos(canvas, evt);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const endDraw = () => {
    setIsDrawing(false);
  };

  const openSignatureModal = (type) => {
    setError('');
    setSuccessMessage('');
    setSignModal({ open: true, type });

    setTimeout(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }, 50);
  };

  const saveSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      setSignModal({ open: false, type: null });
      return;
    }
    const dataUrl = canvas.toDataURL('image/png');
    if (signModal.type === 'tecnico') setTecnicoSignature(dataUrl);
    if (signModal.type === 'cliente') setClienteSignature(dataUrl);
    setSignModal({ open: false, type: null });
  };

  const clearSignaturePreview = (type) => {
    if (type === 'tecnico') setTecnicoSignature(null);
    if (type === 'cliente') setClienteSignature(null);
  };

  // ======================
  // Consumo / recupero gas + notifica (QU / QR / QF)
  // ======================
  const consumeGasAndNotify = async () => {
    const usedName = gasUsedName.trim();
    const recoveredName = gasRecoveredName.trim();

    const usedAmountRaw = String(gasUsedAmount || '').replace(',', '.');
    const recoveredAmountRaw = String(gasRecoveredAmount || '').replace(
      ',',
      '.'
    );

    const usedAmount = Math.round(parseFloat(usedAmountRaw));
    const recoveredAmount = Math.round(parseFloat(recoveredAmountRaw));

    const hasUsed =
      usedName && !Number.isNaN(usedAmount) && usedAmount > 0;
    const hasRecovered =
      recoveredName &&
      !Number.isNaN(recoveredAmount) &&
      recoveredAmount > 0;

    // se non c'è né uso né recupero → non faccio niente
    if (!hasUsed && !hasRecovered) return;

    try {
      // 1) dati utente corrente
      const meRes = await authFetch(`${GAS_API_BASE}/user`);
      if (!meRes.ok) {
        console.error(
          'Impossibile recuperare utente per aggiornare i gas (VIT ASSEGNATI)'
        );
        return;
      }
      const me = await meRes.json();
      const currentUserId = me.id || me._id;

      // 2) livelli gas
      const gasRes = await authFetch(`${GAS_API_BASE}/gas`);
      if (!gasRes.ok) {
        console.error(
          'Impossibile recuperare livelli gas (VIT ASSEGNATI)'
        );
        return;
      }
      const gasData = await gasRes.json();

      const messageParts = [];

      // ========= CASO 1: stesso gas (logica QU / QR / QF) =========
      const sameGas =
        hasUsed &&
        hasRecovered &&
        usedName &&
        recoveredName &&
        usedName === recoveredName;

      if (sameGas || (hasUsed && !hasRecovered) || (!hasUsed && hasRecovered)) {
        // scegliamo un solo nome gas su cui applicare la formula
        const singleName =
          (sameGas && usedName) ||
          (hasUsed && usedName) ||
          (hasRecovered && recoveredName);

        const QU = hasUsed ? usedAmount : 0;
        const QR = hasRecovered ? recoveredAmount : 0;

        // cerco il record gas per questo nome
        let target = gasData.find((g) => {
          const uid = g.user?._id || g.user?.id;
          return uid === currentUserId && g.gasName === singleName;
        });

        // quantità attuale nel furgone (QF)
        let QF = target ? Number(target.quantityGrams || 0) : 0;

        // se non esiste il record e QU > QR → non posso andare sotto zero, quindi loggo e basta
        if (!target && QU > QR) {
          console.warn(
            'Tentativo di usare più gas di quello che esiste (nessun record) per',
            singleName
          );
        } else {
          // calcolo QF_new = QF + QR - QU
          const QF_new = QF + QR - QU;

          // se QU == QR e entrambi > 0 → non faccio nulla
          if (QU === QR && QU > 0) {
            console.log(
              `QU == QR per ${singleName}, QF resta invariato (${QF} g).`
            );
          } else {
            // creo o aggiorno il record
            if (!target) {
              const createRes = await authFetch(`${GAS_API_BASE}/gas`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  gasName: singleName,
                  quantityGrams: Math.max(0, QF_new),
                }),
              });

              if (!createRes.ok) {
                const data = await createRes.json().catch(() => null);
                console.error(
                  'Errore creazione gas (VIT ASSEGNATI, QU/QR/QF):',
                  data || createRes.status
                );
              } else {
                console.log(
                  `Gas "${singleName}" creato con ${Math.max(
                    0,
                    QF_new
                  )} g (VIT ASSEGNATI, QU/QR/QF) per utente`,
                  currentUserId
                );
              }
            } else {
              const updateRes = await authFetch(
                `${GAS_API_BASE}/gas/${target._id}`,
                {
                  method: 'PATCH',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    quantityGrams: Math.max(0, QF_new),
                  }),
                }
              );

              if (!updateRes.ok) {
                const data = await updateRes.json().catch(() => null);
                console.error(
                  'Errore aggiornamento livello gas (VIT ASSEGNATI, QU/QR/QF):',
                  data || updateRes.status
                );
              } else {
                console.log(
                  `Gas "${singleName}" aggiornato da ${QF} g a ${Math.max(
                    0,
                    QF_new
                  )} g (VIT ASSEGNATI, QU/QR/QF) per utente`,
                  currentUserId
                );
              }
            }
          }

          // messaggio testuale per la notifica
          if (hasUsed)
            messageParts.push(`utilizzati ${QU} g di ${singleName}`);
          if (hasRecovered)
            messageParts.push(`recuperati ${QR} g di ${singleName}`);
        }
      }

      // ========= CASO 2: gas diversi (fallback vecchia logica separata) =========
      if (
        hasUsed &&
        hasRecovered &&
        usedName &&
        recoveredName &&
        usedName !== recoveredName
      ) {
        // uso
        const targetUsed = gasData.find((g) => {
          const uid = g.user?._id || g.user?.id;
          return uid === currentUserId && g.gasName === usedName;
        });

        if (!targetUsed) {
          console.warn(
            'Nessun gas con questo nome per il tuo furgone (uso VIT ASSEGNATI, gas diverso):',
            usedName
          );
        } else {
          const currentQty = Number(targetUsed.quantityGrams || 0);
          const newQty = Math.max(0, currentQty - usedAmount);

          const updateRes = await authFetch(
            `${GAS_API_BASE}/gas/${targetUsed._id}`,
            {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ quantityGrams: newQty }),
            }
          );

          if (!updateRes.ok) {
            const data = await updateRes.json().catch(() => null);
            console.error(
              'Errore aggiornamento livello gas (uso VIT ASSEGNATI, gas diverso):',
              data || updateRes.status
            );
          } else {
            console.log(
              `Gas "${usedName}" aggiornato da ${currentQty} g a ${newQty} g (uso VIT ASSEGNATI, gas diverso) per utente`,
              currentUserId
            );
            messageParts.push(
              `utilizzati ${usedAmount} g di ${usedName}`
            );
          }
        }

        // recupero
        let targetRec = gasData.find((g) => {
          const uid = g.user?._id || g.user?.id;
          return uid === currentUserId && g.gasName === recoveredName;
        });

        if (!targetRec) {
          const createRes = await authFetch(`${GAS_API_BASE}/gas`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              gasName: recoveredName,
              quantityGrams: recoveredAmount,
            }),
          });

          if (!createRes.ok) {
            const data = await createRes.json().catch(() => null);
            console.error(
              'Errore creazione gas per recupero (VIT ASSEGNATI, gas diverso):',
              data || createRes.status
            );
          } else {
            targetRec = await createRes.json();
            console.log(
              `Gas "${recoveredName}" creato con ${recoveredAmount} g (recupero VIT ASSEGNATI, gas diverso) per utente`,
              currentUserId
            );
            messageParts.push(
              `recuperati ${recoveredAmount} g di ${recoveredName}`
            );
          }
        } else {
          const currentQtyRec = Number(targetRec.quantityGrams || 0);
          const newQtyRec = currentQtyRec + recoveredAmount;

          const updateRes = await authFetch(
            `${GAS_API_BASE}/gas/${targetRec._id}`,
            {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ quantityGrams: newQtyRec }),
            }
          );

          if (!updateRes.ok) {
            const data = await updateRes.json().catch(() => null);
            console.error(
              'Errore aggiornamento livello gas (recupero VIT ASSEGNATI, gas diverso):',
              data || updateRes.status
            );
          } else {
            console.log(
              `Gas "${recoveredName}" aggiornato da ${currentQtyRec} g a ${newQtyRec} g (recupero VIT ASSEGNATI, gas diverso) per utente`,
              currentUserId
            );
            messageParts.push(
              `recuperati ${recoveredAmount} g di ${recoveredName}`
            );
          }
        }
      }

      // ---- NOTIFICA ----
      if (messageParts.length > 0) {
        const meRes2 = await authFetch(`${GAS_API_BASE}/user`);
        const me2 = meRes2.ok ? await meRes2.json() : null;
        const displayName = getCleanUserName(me2);
        const when = formatNow();

        const message = displayName
          ? `${displayName} ${messageParts.join(
              ' e '
            )} durante un intervento tecnico il ${when}.`
          : `${messageParts.join(
              ' e '
            )} durante un intervento tecnico il ${when}.`;

        try {
          await authFetch(`${GAS_API_BASE}/notifications`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type: 'gas_usage',
              message,
              roles: ['admin', 'gestione', 'segreteria'],
            }),
          });
        } catch (err) {
          console.error(
            'Errore invio notifica utilizzo/recupero gas (VIT ASSEGNATI):',
            err
          );
        }
      }
    } catch (err) {
      console.error(
        'Errore generale durante il consumo/recupero gas (VIT ASSEGNATI):',
        err
      );
    }
  };

  /* --------- genera PDF --------- */
  const handleGeneratePdf = async () => {
    setError('');
    setSuccessMessage('');
    setLoading(true);

    // SENZA RETE. La lavorazione esiste gia' con il suo codice interno,
    // quindi il PDF che il cliente firma lo porta subito.
    if (senzaRete()) {
      try {
        await salvaOffline({
          tipo: TIPI.VIT,
          template: selectedTemplate,
          formData: { ...formData, noteGenerali: '' },
          tecnicoSignature,
          clienteSignature,
          codiceInterno: fullInternalCode || baseCode || '',
          workOrderId,
          datiIniziali: null,
          rotte: { complete: '/api/work/complete-assigned' },
          extraCompletamento: {
            reportType: 'vit',
            notesAppend: formData.noteGenerali,
          },
          summary: {
            baseInternalCode: baseCode,
            iteration,
            codiceEsterno: formData.numeroIntervento,
            data: formData.data,
            cliente: formData.cliente,
            impianto: formData.impianto,
            istituto: formData.impianto,
            luogo: formData.luogo,
            indirizzo: formData.luogo,
            telefono: formData.telefono,
            modello: formData.modello,
            matricola: formData.matricolaImpianto,
            inventario: formData.inventario,
          },
          gas: { gasUsedName, gasUsedAmount, gasRecoveredName, gasRecoveredAmount },
          nomeFile: `verbale_intervento_tecnico_${fullInternalCode || 'da-sincronizzare'}.pdf`,
        });

        setSuccessMessage(
          'Nessuna rete: il verbale e salvato sul dispositivo e verra inviato da solo ' +
            'appena torni online, gas compreso. Il PDF appena scaricato e gia quello ' +
            'definitivo, con il codice interno.'
        );
      } catch (err) {
        console.error('Errore salvataggio offline VIT assegnato:', err);
        setError(err.message || 'Non sono riuscito a salvare il verbale sul dispositivo.');
      } finally {
        setLoading(false);
      }
      return;
    }

    try {

      const pdfFormData = {
        ...formData,
        noteGenerali: '',
        codiceInterno: fullInternalCode || baseCode || '',
      };

      const blob = await generateVitPdfBlob({
        template: selectedTemplate,
        formData: pdfFormData,
        tecnicoSignature,
        clienteSignature,
        inviaReport: true,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `verbale_intervento_tecnico_${fullInternalCode || baseCode || 'SENZA-CODICE'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      setSuccessMessage('VIT generato correttamente.');

      // scala gas + notifica
      await consumeGasAndNotify();

      const newNoteText = (formData.noteGenerali || '').trim();

      let updatedLog = noteLog;

      if (newNoteText) {
        const entry = {
          text: newNoteText,
          userId: null,
          userName: currentUserName || 'Tecnico',
          createdAt: new Date().toISOString(),
          iteration,
        };
      
        updatedLog = [...noteLog, entry];
      }


      // marca la lavorazione assegnata come completata
      if (workOrderId) {
        try {
          await authFetch(`${API_BASE}/work/complete-assigned`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              workOrderId,
              reportType: 'vit',
              summary: {
                // codice interno COMPLETO (es. I25-00000-1)
                codiceInterno: fullInternalCode,
                baseInternalCode: baseCode,
                iteration,
                // Riferimento chiamata / codice esterno
                codiceEsterno: formData.numeroIntervento,
                data: formData.data,
                cliente: formData.cliente,
                impianto: formData.impianto,
                istituto: formData.impianto,
                luogo: formData.luogo,
                indirizzo: formData.luogo,
                telefono: formData.telefono,
                modello: formData.modello,
                matricola: formData.matricolaImpianto,
                inventario: formData.inventario,
              },
              // salviamo tutto lo stato del VIT + firme in WorkInProgress
              formData,
              checks: {},
              tecnicoSignature,
              clienteSignature,
              notesAppend: formData.noteGenerali, // 👈 solo la nota aggiunta ora
            }),
          });
          setNoteLog(updatedLog);
          setFormData((prev) => ({ ...prev, noteGenerali: '' }));
        } catch (err2) {
          console.error(
            'Errore chiamata /work/complete-assigned (VIT ASSEGNATI):',
            err2
          );
        }
      }
    } catch (err) {
      console.error('Errore generazione VIT (assegnato):', err);
      setError(
        err.message || 'Errore inatteso durante la generazione'
      );
    } finally {
      setLoading(false);
    }
  };

  /* --------- render --------- */
  return (
    <div className="min-h-screen bg-gray-100 pb-10">
      <div className="max-w-6xl mx-auto px-4 pt-8">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-1">
              Verbale di Intervento Tecnico
            </h1>
            <p className="text-sm text-gray-600">
              Compilazione da lavorazione assegnata: alcuni campi sono
              stati precompilati.
            </p>
          </div>
          <button
            onClick={() => navigate('/report-assegnati')}
            className="hidden md:inline-flex px-3 py-1.5 rounded-lg text-xs bg-gray-100 text-gray-700 hover:bg-gray-200"
          >
            ← Indietro
          </button>
        </div>

        <p className="text-xs text-gray-500 mb-6 md:hidden">
          Alcuni dati provengono dalla lavorazione assegnata.
        </p>

        {/* Selezione template */}
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          {templates.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setSelectedTemplate(t.id)}
              className={`w-full text-left p-4 rounded-xl border transition shadow-sm ${
                selectedTemplate === t.id
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-200 bg-white hover:border-blue-400'
              }`}
            >
              <h2 className="font-semibold text-gray-800">{t.label}</h2>
              <p className="text-xs text-gray-500 mt-1">
                Modello PDF: {t.file.replace('/VIT/', '')}
              </p>
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-[2.1fr,3fr] gap-8">
          {/* SX: form */}
          <div className="space-y-6">
            {/* Dati intervento */}
            <Card className="shadow-sm p-4 overflow-hidden">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                Dati intervento
              </h3>

              {/* GAS UTILIZZATO / RECUPERATO */}
              <section className="bg-white rounded-2xl shadow-sm p-4 md:p-6 space-y-4 mb-4">
                <h2 className="text-lg font-semibold text-gray-800">
                  Gas utilizzato (solo per tracciamento interno)
                </h2>
                <p className="text-sm text-gray-500">
                  Questi dati non vengono scritti sul PDF. Servono solo
                  per scalare o incrementare i livelli gas del tuo
                  furgone e avvisare l&apos;azienda.
                </p>

                {/* GAS UTILIZZATO */}
                <div className="mt-2 space-y-3">
                  <h3 className="text-sm font-semibold text-gray-700">
                    Gas utilizzato
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Tipo di gas
                      </label>
                      <select
                        value={gasUsedName}
                        onChange={(e) => setGasUsedName(e.target.value)}
                        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      >
                        <option value="">
                          Nessun gas utilizzato
                        </option>
                        {availableGases.map((name) => (
                          <option key={name} value={name}>
                            {name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Quantità utilizzata (g)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={gasUsedAmount}
                        onChange={(e) =>
                          setGasUsedAmount(e.target.value)
                        }
                        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Es. 250"
                      />
                    </div>
                  </div>
                </div>

                {/* GAS RECUPERATO */}
                <div className="mt-4 border-t border-gray-100 pt-4 space-y-3">
                  <h3 className="text-sm font-semibold text-gray-700">
                    Gas recuperato (opzionale)
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Tipo di gas
                      </label>
                      <select
                        value={gasRecoveredName}
                        onChange={(e) =>
                          setGasRecoveredName(e.target.value)
                        }
                        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      >
                        <option value="">
                          Nessun gas recuperato
                        </option>
                        {availableGases.map((name) => (
                          <option key={name} value={name}>
                            {name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Quantità recuperata (g)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={gasRecoveredAmount}
                        onChange={(e) =>
                          setGasRecoveredAmount(e.target.value)
                        }
                        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Es. 200"
                      />
                    </div>
                  </div>
                </div>
              </section>

              {/* Altri dati intervento */}
              <div className="space-y-3 text-sm">
                {/* Codice interno (read-only) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Codice interno
                  </label>
                  <input
                    type="text"
                    value={fullInternalCode}
                    readOnly
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-700"
                  />
                </div>

                {[
                  { name: 'data', label: 'Data intervento', type: 'date' },
                  { name: 'cliente', label: 'Cliente' },
                  { name: 'impianto', label: 'Istituto' },
                  { name: 'luogo', label: 'Indirizzo' },
                  { name: 'telefono', label: 'Telefono' },
                  {
                    name: 'numeroIntervento',
                    label: 'Riferimento chiamata',
                  },
                  { name: 'modello', label: 'Modello' },
                  {
                    name: 'matricolaImpianto',
                    label: 'Matricola',
                  },
                  { name: 'inventario', label: 'Inventario' },
                ].map((field) => (
                  <div key={field.name}>
                    <label className="block text-sm font-medium text-gray-700">
                      {field.label}
                    </label>
                    <input
                      type={field.type || 'text'}
                      name={field.name}
                      value={formData[field.name] || ''}
                      onChange={handleChange}
                      className={
                        'mt-1 w-full max-w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ' +
                        (field.type === 'date' ? 'appearance-none' : '')
                      }
                    />
                  </div>
                ))}

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Note generali (uso interno, NON sul PDF)
                  </label>

                  {/* Log storico note */}
                  {noteLog.length > 0 ? (
                    <div className="mt-1 mb-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg bg-gray-50 px-3 py-2 text-xs space-y-1">
                      {noteLog.map((entry, idx) => {
                        const date = entry.createdAt
                          ? new Date(entry.createdAt)
                          : null;
                        const formattedDate = date
                          ? date.toLocaleDateString('it-IT')
                          : '-';
                        const name = entry.userName || 'Utente sconosciuto';
                        return (
                          <div key={idx}>
                            <span className="font-semibold">
                              [{formattedDate}] {name}:
                            </span>{' '}
                            {entry.text}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="mt-1 mb-2 text-xs text-gray-400">
                      Nessuna nota precedente registrata per questa lavorazione.
                    </p>
                  )}

                  {/* Nuova nota per questo giro */}
                  <textarea
                    name="noteGenerali"
                    value={formData.noteGenerali}
                    onChange={handleChange}
                    rows={2}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Aggiungi qui le note per il prossimo tecnico (non verranno stampate sul PDF)."
                  />
                </div>
              </div>
            </Card>

            {/* Costi intervento */}
            <Card className="shadow-sm p-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                Costi intervento
              </h3>
              <div className="space-y-3 text-sm">
                {/* Diritto fisso chiamata: numero + euro */}
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-gray-700">
                    Diritto fisso chiamata
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      name="dirittoFissoQuantita"
                      value={formData.dirittoFissoQuantita || ''}
                      onChange={handleQuantityChange('dirittoFissoQuantita')}
                      maxLength={2}
                      placeholder="Qtà"
                      className="w-16 border border-gray-300 rounded-lg px-2 py-2 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="text"
                      name="dirittoFissoEuro"
                      value={formData.dirittoFissoEuro || ''}
                      onChange={handleChange}
                      onBlur={() =>
                        setFormData((prev) => ({
                          ...prev,
                          dirittoFissoEuro: formatEuro(prev.dirittoFissoEuro),
                        }))
                      }
                      placeholder="Euro"
                      className="w-28 border border-gray-300 rounded-lg px-3 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                    
                {/* Ore di lavoro: numero (max 2) + euro */}
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-gray-700">
                    Ore di lavoro in sito
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      name="oreLavoroQuantita"
                      value={formData.oreLavoroQuantita || ''}
                      onChange={handleQuantityChange('oreLavoroQuantita')}
                      maxLength={2}
                      placeholder="h"
                      className="w-16 border border-gray-300 rounded-lg px-2 py-2 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="text"
                      name="oreLavoroEuro"
                      value={formData.oreLavoroEuro || ''}
                      onChange={handleChange}
                      onBlur={() =>
                        setFormData((prev) => ({
                          ...prev,
                          oreLavoroEuro: formatEuro(prev.oreLavoroEuro),
                        }))
                      }
                      placeholder="Euro"
                      className="w-28 border border-gray-300 rounded-lg px-3 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            </Card>


            {/* Ricambi */}
            <Card className="shadow-sm p-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                Pezzi di ricambio
              </h3>
              <p className="text-xs text-gray-500 mb-2">
                Compila fino a 8 righe con quantità, descrizione e importo.
              </p>

              <div className="overflow-x-auto">
                <div className="min-w-[320px] space-y-2 text-xs md:text-sm">
                  <div className="grid grid-cols-[4rem,minmax(0,1fr),5.5rem] gap-2 font-medium text-gray-700">
                    <span className="text-center">Qtà</span>
                    <span className="text-center">
                      Descrizione pezzo
                    </span>
                    <span className="text-right">Euro</span>
                  </div>

                  {formData.ricambi.map((riga, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-[4rem,minmax(0,1fr),5.5rem] gap-2"
                    >
                      {/* Quantità: max 4 digits */}
                      <input
                        type="text"
                        value={riga.quantita}
                        maxLength={4}
                        onChange={(e) =>
                          handleRicambioChange(
                            index,
                            'quantita',
                            e.target.value
                          )
                        }
                        className="border border-gray-300 rounded-lg px-2 py-1 text-xs md:text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="1"
                      />

                      {/* Descrizione */}
                      <input
                        type="text"
                        value={riga.descrizione}
                        onChange={(e) =>
                          handleRicambioChange(
                            index,
                            'descrizione',
                            e.target.value
                          )
                        }
                        className="border border-gray-300 rounded-lg px-2 py-1 text-xs md:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Descrizione pezzo"
                      />

                      {/* Euro */}
                      <input
                        type="text"
                        value={riga.euro}
                        onChange={(e) =>
                          handleRicambioChange(
                            index,
                            'euro',
                            e.target.value
                          )
                        }
                        onBlur={() =>
                          handleRicambioChange(
                            index,
                            'euro',
                            formatEuro(riga.euro)
                          )
                        }
                        className="border border-gray-300 rounded-lg px-2 py-1 text-xs md:text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="0,00"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </Card>

            <DatiTrasferta formData={formData} onChange={handleChange} />

            {/* Lavori eseguiti + firme + bottone */}
            <Card className="shadow-sm p-4 space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Lavori eseguiti
                </h3>
                <textarea
                  name="lavoriEseguiti"
                  value={formData.lavoriEseguiti}
                  onChange={handleChange}
                  rows={4}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Descrivi le attività svolte, controlli effettuati, eventuali note al cliente..."
                />
              </div>

              {/* Firme */}
              <div className="space-y-3 text-sm border-t border-gray-200 pt-3">
                <h3 className="text-lg font-semibold text-gray-800">
                  Firme
                </h3>
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-medium text-gray-700">
                        Tecnico
                      </p>
                      {tecnicoSignature ? (
                        <img
                          src={tecnicoSignature}
                          alt="Firma tecnico"
                          className="h-16 mt-1 border border-gray-200 rounded bg-white"
                        />
                      ) : (
                        <p className="text-xs text-gray-400 mt-1">
                          Nessuna firma registrata.
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col gap-1">
                      <button
                        type="button"
                        onClick={() =>
                          openSignatureModal('tecnico')
                        }
                        className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700"
                      >
                        Firma tecnico
                      </button>
                      {tecnicoSignature && (
                        <button
                          type="button"
                          onClick={() =>
                            clearSignaturePreview('tecnico')
                          }
                          className="text-[11px] text-gray-500 hover:text-gray-700"
                        >
                          Cancella firma
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-3 border-t border-gray-200 pt-3">
                    <div>
                      <p className="font-medium text-gray-700">
                        Cliente
                      </p>
                      {clienteSignature ? (
                        <img
                          src={clienteSignature}
                          alt="Firma cliente"
                          className="h-16 mt-1 border border-gray-200 rounded bg-white"
                        />
                      ) : (
                        <p className="text-xs text-gray-400 mt-1">
                          Nessuna firma registrata.
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col gap-1">
                      <button
                        type="button"
                        onClick={() =>
                          openSignatureModal('cliente')
                        }
                        className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700"
                      >
                        Firma cliente
                      </button>
                      {clienteSignature && (
                        <button
                          type="button"
                          onClick={() =>
                            clearSignaturePreview('cliente')
                          }
                          className="text-[11px] text-gray-500 hover:text-gray-700"
                        >
                          Cancella firma
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {error && (
                  <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    {error}
                  </p>
                )}
                {successMessage && (
                  <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                    {successMessage}
                  </p>
                )}

                <button
                  type="button"
                  onClick={handleGeneratePdf}
                  disabled={loading}
                  className={`w-full py-2.5 rounded-lg text-sm font-semibold text-white shadow-sm transition ${
                    loading
                      ? 'bg-blue-300 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {loading
                    ? 'Generazione in corso...'
                    : 'Genera VIT in PDF'}
                </button>
              </div>
            </Card>
          </div>

          {/* DX: anteprima pdf */}
          <Card className="shadow-sm p-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Anteprima modello
            </h3>
            {pdfUrl ? (
              <div className="border border-gray-200 rounded-lg overflow-hidden h-[600px]">
                <iframe
                  title="Anteprima PDF VIT"
                  src={pdfUrl}
                  className="w-full h-full"
                />
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                Seleziona un modello per visualizzare
                l&apos;anteprima.
              </p>
            )}
          </Card>
        </div>
      </div>

      {/* Modal firma full-screen */}
      {signModal.open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
          onTouchMove={(e) => e.preventDefault()}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-4">
            <div className="flex items-center justify_between mb-2">
              <h2 className="text-sm font-semibold text-gray-800">
                Firma{' '}
                {signModal.type === 'tecnico'
                  ? 'tecnico'
                  : 'cliente'}
              </h2>
              <button
                onClick={() =>
                  setSignModal({ open: false, type: null })
                }
                className="text-gray-500 hover:text-gray-800 text-xl leading-none"
              >
                &times;
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-2">
              Firma con il dito o con il mouse. Al termine premi
              &quot;Salva firma&quot;.
            </p>
            <div className="border border-gray-300 rounded-lg bg-gray-50">
              <canvas
                ref={canvasRef}
                width={400}
                height={200}
                className="w-full h-48"
                onMouseDown={startDraw}
                onMouseMove={moveDraw}
                onMouseUp={endDraw}
                onMouseLeave={endDraw}
                onTouchStart={startDraw}
                onTouchMove={moveDraw}
                onTouchEnd={endDraw}
              />
            </div>
            <div className="mt-3 flex justify-between items-center">
              <button
                type="button"
                onClick={() => {
                  const canvas = canvasRef.current;
                  if (!canvas) return;
                  const ctx = canvas.getContext('2d');
                  ctx.clearRect(0, 0, canvas.width, canvas.height);
                }}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Cancella
              </button>
              <button
                type="button"
                onClick={saveSignature}
                className="px-4 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700"
              >
                Salva firma
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VitAssegnati;
